import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FileText, Download, Save, Loader2, Euro, User, Building2,
  Calculator, Eye, Trash2, ChevronDown, ChevronUp, Landmark,
  Upload, FileUp, ScanLine, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STEUERKLASSEN = [
  { value: '1', label: 'I - Ledig' },
  { value: '2', label: 'II - Alleinerziehend' },
  { value: '3', label: 'III - Verheiratet (Alleinverdiener)' },
  { value: '4', label: 'IV - Verheiratet (beide verdienen)' },
  { value: '5', label: 'V - Verheiratet (Zweitverdiener)' },
  { value: '6', label: 'VI - Zweitjob' },
];

const KONFESSIONEN = [
  { value: 'keine', label: 'Keine' },
  { value: 'ev', label: 'Evangelisch' },
  { value: 'rk', label: 'Römisch-Katholisch' },
];

const KRANKENKASSEN = [
  'AOK', 'TK (Techniker)', 'BARMER', 'DAK-Gesundheit', 'IKK Classic',
  'KKH', 'HEK', 'Knappschaft', 'BKK', 'Andere'
];

function fmt(val) {
  return Number(val || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 hover:bg-gray-50">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#9B1B30]" />}
          <span className="font-semibold text-sm">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-gray-100">{children}</div>}
    </div>
  );
};

export default function AdminLohnabrechnung() {
  const [activeTab, setActiveTab] = useState('neu');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(null); // 'payslip' | 'contract' | null
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [archive, setArchive] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [calculation, setCalculation] = useState(null);

  const payslipInputRef = useRef(null);
  const contractInputRef = useRef(null);

  const [employer, setEmployer] = useState({
    firmenname: '', firmenadresse: '', steuernummer: '', betriebsnummer: ''
  });

  const [employee, setEmployee] = useState({
    personal_nr: '00001', vorname: '', nachname: '', adresse: '',
    geburtsdatum: '', steuer_id: '', sv_nummer: '', krankenkasse: 'AOK',
    steuerklasse: '1', kinderfreibetrag: '0', konfession: 'keine',
    eintrittsdatum: '', austritt: ''
  });

  const [bank, setBank] = useState({ bank_name: '', bank_bic: '', bank_iban: '' });

  const [payroll, setPayroll] = useState({
    abrechnungsmonat: new Date().toISOString().slice(0, 7),
    arbeitsstunden: '160', stundenlohn: ''
  });

  const brutto = Number(payroll.arbeitsstunden || 0) * Number(payroll.stundenlohn || 0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, teamRes] = await Promise.all([
          api.get('/admin/documents/settings'),
          api.get('/admin/team-members')
        ]);
        const s = settingsRes.data;
        setEmployer({
          firmenname: s.company_name || '',
          firmenadresse: `${s.company_street || ''} ${s.company_city || ''}`.trim(),
          steuernummer: s.company_vat_id || '',
          betriebsnummer: ''
        });
        setTeamMembers(teamRes.data || []);
      } catch (e) { console.error('Load failed:', e); }
    };
    loadData();
    loadArchive();
  }, []);

  const doCalculate = useCallback(async () => {
    if (brutto <= 0) { setCalculation(null); return; }
    try {
      const res = await api.post('/admin/payroll/calculate', {
        brutto,
        steuerklasse: parseInt(employee.steuerklasse),
        kinderfreibetrag: parseFloat(employee.kinderfreibetrag || '0'),
        konfession: employee.konfession,
      });
      setCalculation(res.data);
    } catch (e) { console.error('Calc error:', e); }
  }, [brutto, employee.steuerklasse, employee.kinderfreibetrag, employee.konfession]);

  useEffect(() => {
    const timer = setTimeout(doCalculate, 300);
    return () => clearTimeout(timer);
  }, [doCalculate]);

  const loadArchive = async () => {
    try {
      const res = await api.get('/admin/payroll');
      setArchive(res.data || []);
    } catch (e) { console.error('Archive error:', e); }
  };

  const handleMemberSelect = (memberId) => {
    setSelectedMember(memberId);
    const member = teamMembers.find(m => m.id === memberId);
    if (member) {
      const nameParts = (member.name || '').split(' ');
      setEmployee(prev => ({
        ...prev,
        vorname: nameParts[0] || '',
        nachname: nameParts.slice(1).join(' ') || '',
      }));
      toast.success(`${member.name} ausgewählt`);
    }
  };

  // File to base64
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Upload and analyze payslip
  const handlePayslipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Datei zu groß (max. 10 MB)');
      return;
    }

    setAnalyzing('payslip');
    try {
      const base64 = await fileToBase64(file);
      const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
      const res = await api.post('/admin/payroll/analyze-payslip', { 
        image_base64: base64,
        file_type: isPdf ? 'pdf' : 'image'
      });
      const d = res.data.extracted_data;

      // Fill in all extracted fields
      if (d.personal_nr) setEmployee(p => ({ ...p, personal_nr: d.personal_nr }));
      if (d.vorname) setEmployee(p => ({ ...p, vorname: d.vorname }));
      if (d.nachname) setEmployee(p => ({ ...p, nachname: d.nachname }));
      if (d.adresse) setEmployee(p => ({ ...p, adresse: d.adresse }));
      if (d.geburtsdatum) setEmployee(p => ({ ...p, geburtsdatum: d.geburtsdatum }));
      if (d.steuer_id) setEmployee(p => ({ ...p, steuer_id: d.steuer_id }));
      if (d.sv_nummer) setEmployee(p => ({ ...p, sv_nummer: d.sv_nummer }));
      if (d.krankenkasse) setEmployee(p => ({ ...p, krankenkasse: d.krankenkasse }));
      if (d.steuerklasse) setEmployee(p => ({ ...p, steuerklasse: String(d.steuerklasse) }));
      if (d.kinderfreibetrag) setEmployee(p => ({ ...p, kinderfreibetrag: String(d.kinderfreibetrag) }));
      if (d.konfession) setEmployee(p => ({ ...p, konfession: d.konfession || 'keine' }));
      if (d.eintrittsdatum) setEmployee(p => ({ ...p, eintrittsdatum: d.eintrittsdatum }));
      if (d.firmenname) setEmployer(p => ({ ...p, firmenname: d.firmenname }));
      if (d.firmenadresse) setEmployer(p => ({ ...p, firmenadresse: d.firmenadresse }));
      if (d.steuernummer) setEmployer(p => ({ ...p, steuernummer: d.steuernummer }));
      if (d.betriebsnummer) setEmployer(p => ({ ...p, betriebsnummer: d.betriebsnummer }));
      if (d.bank_name) setBank(p => ({ ...p, bank_name: d.bank_name }));
      if (d.bank_bic) setBank(p => ({ ...p, bank_bic: d.bank_bic }));
      if (d.bank_iban) setBank(p => ({ ...p, bank_iban: d.bank_iban }));
      if (d.abrechnungsmonat) setPayroll(p => ({ ...p, abrechnungsmonat: d.abrechnungsmonat }));
      if (d.arbeitsstunden) setPayroll(p => ({ ...p, arbeitsstunden: String(d.arbeitsstunden) }));
      if (d.stundenlohn) setPayroll(p => ({ ...p, stundenlohn: String(d.stundenlohn) }));

      toast.success('Lohnabrechnung erfolgreich analysiert! Bitte Daten prüfen.', { duration: 5000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(null);
    }
  };

  // Upload and analyze contract
  const handleContractUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Datei zu groß (max. 10 MB)');
      return;
    }

    setAnalyzing('contract');
    try {
      const base64 = await fileToBase64(file);
      const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
      const res = await api.post('/admin/payroll/analyze-contract', { 
        image_base64: base64,
        file_type: isPdf ? 'pdf' : 'image'
      });
      const d = res.data.extracted_data;

      if (d.vorname) setEmployee(p => ({ ...p, vorname: d.vorname }));
      if (d.nachname) setEmployee(p => ({ ...p, nachname: d.nachname }));
      if (d.adresse) setEmployee(p => ({ ...p, adresse: d.adresse }));
      if (d.geburtsdatum) setEmployee(p => ({ ...p, geburtsdatum: d.geburtsdatum }));
      if (d.eintrittsdatum) setEmployee(p => ({ ...p, eintrittsdatum: d.eintrittsdatum }));
      if (d.firmenname) setEmployer(p => ({ ...p, firmenname: d.firmenname }));
      if (d.firmenadresse) setEmployer(p => ({ ...p, firmenadresse: d.firmenadresse }));
      if (d.stundenlohn) setPayroll(p => ({ ...p, stundenlohn: String(d.stundenlohn) }));
      if (d.arbeitsstunden_monat) setPayroll(p => ({ ...p, arbeitsstunden: String(Math.round(Number(d.arbeitsstunden_monat))) }));

      let info = 'Arbeitsvertrag analysiert!';
      if (d.taetigkeit) info += ` Tätigkeit: ${d.taetigkeit}.`;
      if (d.urlaubstage) info += ` Urlaub: ${d.urlaubstage} Tage/Jahr.`;
      if (d.zusatzinfo) info += ` ${d.zusatzinfo}`;
      toast.success(info, { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(null);
    }
  };

  const generatePdf = async (save = false) => {
    if (brutto <= 0) { toast.error('Bitte Stundenlohn und Arbeitsstunden eingeben'); return; }
    if (!employee.vorname || !employee.nachname) { toast.error('Bitte Mitarbeitername eingeben'); return; }

    save ? setSaving(true) : setLoading(true);
    try {
      const res = await api.post('/admin/payroll/generate', {
        ...employer, ...employee, ...bank,
        steuerklasse: parseInt(employee.steuerklasse),
        kinderfreibetrag: parseFloat(employee.kinderfreibetrag || '0'),
        ...payroll, brutto, save,
        team_member_id: selectedMember || null,
      });
      setPreviewPdf(res.data.pdf_base64);
      setPreviewOpen(true);
      if (save) { toast.success('Lohnabrechnung gespeichert'); loadArchive(); }
      else { toast.success('PDF erstellt'); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehlgeschlagen');
    } finally { setLoading(false); setSaving(false); }
  };

  const downloadPdf = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${base64Data}`;
    link.download = filename;
    link.click();
  };

  const downloadArchivePdf = async (rec) => {
    try {
      const res = await api.get(`/admin/payroll/${rec.id}/pdf`);
      downloadPdf(res.data.pdf_base64, `Lohnabrechnung_${rec.nachname}_${rec.abrechnungsmonat}.pdf`);
    } catch (e) { toast.error('PDF konnte nicht geladen werden'); }
  };

  const deletePayroll = async (rec) => {
    if (!confirm(`Lohnabrechnung für ${rec.vorname} ${rec.nachname} (${rec.abrechnungsmonat_display}) wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/payroll/${rec.id}`);
      toast.success('Gelöscht');
      loadArchive();
    } catch (e) { toast.error('Löschen fehlgeschlagen'); }
  };

  const Field = ({ label, value, onChange, type = 'text', placeholder = '', className = '', ...props }) => (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-xs text-gray-600">{label}</Label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="h-9 text-sm" {...props} />
    </div>
  );

  return (
    <div className="space-y-4 max-w-6xl mx-auto" data-testid="payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Euro className="w-6 h-6 text-[#9B1B30]" />
          Lohnabrechnung
        </h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="neu" data-testid="tab-new">Neue Abrechnung</TabsTrigger>
          <TabsTrigger value="archiv" data-testid="tab-archive">Archiv ({archive.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="neu" className="space-y-4 mt-4">
          {/* Import Buttons */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50/50">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <input ref={payslipInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handlePayslipUpload} />
                <input ref={contractInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleContractUpload} />

                <Button
                  variant="outline"
                  className="flex-1 h-14 border-[#9B1B30]/30 hover:border-[#9B1B30] hover:bg-[#9B1B30]/5"
                  onClick={() => payslipInputRef.current?.click()}
                  disabled={analyzing !== null}
                  data-testid="btn-upload-payslip"
                >
                  {analyzing === 'payslip' ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin text-[#9B1B30]" />
                  ) : (
                    <ScanLine className="w-5 h-5 mr-2 text-[#9B1B30]" />
                  )}
                  <div className="text-left">
                    <div className="font-medium text-sm">Abrechnung importieren</div>
                    <div className="text-xs text-gray-500">Vorhandene Lohnabrechnung hochladen</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="flex-1 h-14 border-blue-300 hover:border-blue-500 hover:bg-blue-50"
                  onClick={() => contractInputRef.current?.click()}
                  disabled={analyzing !== null}
                  data-testid="btn-upload-contract"
                >
                  {analyzing === 'contract' ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin text-blue-600" />
                  ) : (
                    <FileCheck className="w-5 h-5 mr-2 text-blue-600" />
                  )}
                  <div className="text-left">
                    <div className="font-medium text-sm">Arbeitsvertrag importieren</div>
                    <div className="text-xs text-gray-500">Vertrag analysieren & Lohn übernehmen</div>
                  </div>
                </Button>
              </div>
              {analyzing && (
                <div className="mt-3 flex items-center gap-2 text-sm text-[#9B1B30] animate-pulse">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  KI analysiert {analyzing === 'payslip' ? 'die Lohnabrechnung' : 'den Arbeitsvertrag'}...
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team member select */}
          {teamMembers.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <Label className="text-xs text-gray-600 mb-2 block">Mitarbeiter auswählen</Label>
                <Select value={selectedMember} onValueChange={handleMemberSelect}>
                  <SelectTrigger className="h-9" data-testid="member-select">
                    <SelectValue placeholder="Mitarbeiter auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name} – {m.role || 'Mitarbeiter'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Section title="Arbeitgeber" icon={Building2}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <Field label="Firmenname" value={employer.firmenname} onChange={v => setEmployer(p => ({...p, firmenname: v}))} data-testid="input-firmenname" />
                  <Field label="Firmenadresse" value={employer.firmenadresse} onChange={v => setEmployer(p => ({...p, firmenadresse: v}))} data-testid="input-firmenadresse" />
                  <Field label="Steuernummer" value={employer.steuernummer} onChange={v => setEmployer(p => ({...p, steuernummer: v}))} data-testid="input-steuernummer" />
                  <Field label="Betriebsnummer" value={employer.betriebsnummer} onChange={v => setEmployer(p => ({...p, betriebsnummer: v}))} data-testid="input-betriebsnummer" />
                </div>
              </Section>

              <Section title="Mitarbeiter" icon={User}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <Field label="Personalnummer" value={employee.personal_nr} onChange={v => setEmployee(p => ({...p, personal_nr: v}))} placeholder="00001" data-testid="input-personal-nr" />
                  <div />
                  <Field label="Vorname" value={employee.vorname} onChange={v => setEmployee(p => ({...p, vorname: v}))} data-testid="input-vorname" />
                  <Field label="Nachname" value={employee.nachname} onChange={v => setEmployee(p => ({...p, nachname: v}))} data-testid="input-nachname" />
                  <Field label="Adresse" value={employee.adresse} onChange={v => setEmployee(p => ({...p, adresse: v}))} className="sm:col-span-2" data-testid="input-adresse" />
                  <Field label="Geburtsdatum" type="date" value={employee.geburtsdatum} onChange={v => setEmployee(p => ({...p, geburtsdatum: v}))} data-testid="input-geburtsdatum" />
                  <Field label="Eintrittsdatum" type="date" value={employee.eintrittsdatum} onChange={v => setEmployee(p => ({...p, eintrittsdatum: v}))} data-testid="input-eintrittsdatum" />
                  <Field label="Steuer-ID" value={employee.steuer_id} onChange={v => setEmployee(p => ({...p, steuer_id: v}))} placeholder="z.B. 12 345 678 901" data-testid="input-steuer-id" />
                  <Field label="Sozialversicherungsnummer" value={employee.sv_nummer} onChange={v => setEmployee(p => ({...p, sv_nummer: v}))} placeholder="z.B. 12 010190 M 001" data-testid="input-sv-nummer" />
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Krankenkasse</Label>
                    <Select value={employee.krankenkasse} onValueChange={v => setEmployee(p => ({...p, krankenkasse: v}))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="select-krankenkasse"><SelectValue /></SelectTrigger>
                      <SelectContent>{KRANKENKASSEN.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Steuerklasse</Label>
                    <Select value={employee.steuerklasse} onValueChange={v => setEmployee(p => ({...p, steuerklasse: v}))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="select-steuerklasse"><SelectValue /></SelectTrigger>
                      <SelectContent>{STEUERKLASSEN.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Field label="Kinderfreibetrag" type="number" value={employee.kinderfreibetrag} onChange={v => setEmployee(p => ({...p, kinderfreibetrag: v}))} step="0.5" min="0" data-testid="input-kinderfreibetrag" />
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Konfession</Label>
                    <Select value={employee.konfession} onValueChange={v => setEmployee(p => ({...p, konfession: v}))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="select-konfession"><SelectValue placeholder="Keine" /></SelectTrigger>
                      <SelectContent>{KONFESSIONEN.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </Section>

              <Section title="Bankverbindung" icon={Landmark}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <Field label="Bank" value={bank.bank_name} onChange={v => setBank(p => ({...p, bank_name: v}))} placeholder="z.B. Sparkasse" data-testid="input-bank-name" />
                  <Field label="BIC" value={bank.bank_bic} onChange={v => setBank(p => ({...p, bank_bic: v}))} placeholder="z.B. COBADEFFXXX" data-testid="input-bank-bic" />
                  <Field label="IBAN" value={bank.bank_iban} onChange={v => setBank(p => ({...p, bank_iban: v}))} placeholder="z.B. DE89 3704 0044 0532 0130 00" data-testid="input-bank-iban" />
                </div>
              </Section>

              <Section title="Abrechnungsdaten" icon={Calculator}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Abrechnungsmonat</Label>
                    <Input type="month" value={payroll.abrechnungsmonat} onChange={e => setPayroll(p => ({...p, abrechnungsmonat: e.target.value}))} className="h-9 text-sm" data-testid="input-monat" />
                  </div>
                  <Field label="Arbeitsstunden" type="number" value={payroll.arbeitsstunden} onChange={v => setPayroll(p => ({...p, arbeitsstunden: v}))} placeholder="160" data-testid="input-stunden" />
                  <Field label="Stundenlohn (€)" type="number" value={payroll.stundenlohn} onChange={v => setPayroll(p => ({...p, stundenlohn: v}))} placeholder="z.B. 15.00" step="0.01" data-testid="input-stundenlohn" />
                </div>
                {brutto > 0 && (
                  <div className="mt-3 p-3 bg-[#9B1B30]/5 rounded-lg border border-[#9B1B30]/20">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Bruttolohn berechnet:</span>
                      <span className="text-lg font-bold text-[#9B1B30]" data-testid="brutto-display">{fmt(brutto)} €</span>
                    </div>
                  </div>
                )}
              </Section>
            </div>

            {/* Right: Live calculation */}
            <div className="space-y-4">
              <Card className="sticky top-4 border-[#9B1B30]/20">
                <CardHeader className="pb-2 bg-[#9B1B30]/5">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-[#9B1B30]" />
                    Berechnung
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2 text-sm">
                  {calculation ? (
                    <>
                      <div className="flex justify-between py-1 border-b"><span>Bruttolohn</span><span className="font-medium">{fmt(calculation.brutto)} €</span></div>
                      <div className="text-xs text-gray-500 uppercase font-semibold pt-2">Steuern</div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">Lohnsteuer</span><span>- {fmt(calculation.lohnsteuer)} €</span></div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">Soli</span><span>- {fmt(calculation.solidaritaetszuschlag)} €</span></div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">Kirchensteuer</span><span>- {fmt(calculation.kirchensteuer)} €</span></div>
                      <div className="flex justify-between py-1 border-b text-xs font-medium"><span>Steuern gesamt</span><span>- {fmt(calculation.steuer_gesamt)} €</span></div>
                      <div className="text-xs text-gray-500 uppercase font-semibold pt-2">Sozialversicherung</div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">KV</span><span>- {fmt(calculation.krankenversicherung_an)} €</span></div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">RV</span><span>- {fmt(calculation.rentenversicherung_an)} €</span></div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">AV</span><span>- {fmt(calculation.arbeitslosenversicherung_an)} €</span></div>
                      <div className="flex justify-between py-0.5 text-xs"><span className="text-gray-600">PV</span><span>- {fmt(calculation.pflegeversicherung_an)} €</span></div>
                      <div className="flex justify-between py-1 border-b text-xs font-medium"><span>SV gesamt</span><span>- {fmt(calculation.sozialversicherung_gesamt)} €</span></div>
                      <div className="flex justify-between py-1 border-b font-medium text-red-600"><span>Gesamtabzüge</span><span>- {fmt(calculation.gesamt_abzuege)} €</span></div>
                      <div className="flex justify-between py-2 bg-[#9B1B30] text-white rounded px-3 -mx-1 mt-2">
                        <span className="font-bold">Nettolohn</span>
                        <span className="font-bold text-lg" data-testid="netto-display">{fmt(calculation.netto)} €</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-gray-400 py-8 text-xs">Stundenlohn eingeben</div>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Button onClick={() => generatePdf(false)} disabled={loading || brutto <= 0} className="w-full bg-[#9B1B30] hover:bg-[#7d1526]" data-testid="btn-preview">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  PDF Vorschau
                </Button>
                <Button onClick={() => generatePdf(true)} disabled={saving || brutto <= 0} variant="outline" className="w-full border-[#9B1B30] text-[#9B1B30]" data-testid="btn-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Speichern & PDF
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archiv" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Gespeicherte Lohnabrechnungen</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="block md:hidden space-y-3">
                {archive.map(rec => (
                  <div key={rec.id} className="border rounded-lg p-3 bg-gray-50" data-testid={`archive-card-${rec.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm">{rec.vorname} {rec.nachname}</div>
                        <div className="text-xs text-gray-500">{rec.abrechnungsmonat_display}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Netto</div>
                        <div className="font-bold text-[#9B1B30] text-sm">{fmt(rec.netto)} €</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => downloadArchivePdf(rec)}>
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 text-xs text-red-500" onClick={() => deletePayroll(rec)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {archive.length === 0 && <div className="text-center text-gray-400 py-8 text-sm">Keine Abrechnungen vorhanden</div>}
              </div>

              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-sm">Mitarbeiter</TableHead>
                      <TableHead className="text-sm">Monat</TableHead>
                      <TableHead className="text-right text-sm">Brutto</TableHead>
                      <TableHead className="text-right text-sm">Abzüge</TableHead>
                      <TableHead className="text-right text-sm">Netto</TableHead>
                      <TableHead className="text-sm">Erstellt</TableHead>
                      <TableHead className="text-sm">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archive.map(rec => (
                      <TableRow key={rec.id} data-testid={`archive-row-${rec.id}`}>
                        <TableCell className="font-medium text-sm">{rec.vorname} {rec.nachname}</TableCell>
                        <TableCell className="text-sm">{rec.abrechnungsmonat_display}</TableCell>
                        <TableCell className="text-right text-sm">{fmt(rec.brutto)} €</TableCell>
                        <TableCell className="text-right text-sm text-red-600">- {fmt(rec.gesamt_abzuege)} €</TableCell>
                        <TableCell className="text-right font-bold text-sm text-[#9B1B30]">{fmt(rec.netto)} €</TableCell>
                        <TableCell className="text-sm text-gray-500">{new Date(rec.created_at).toLocaleDateString('de-DE')}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadArchivePdf(rec)} title="PDF herunterladen" data-testid={`download-${rec.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deletePayroll(rec)} className="text-red-500" title="Löschen" data-testid={`delete-${rec.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {archive.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8 text-sm">Keine Abrechnungen vorhanden</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#9B1B30]" />
              Lohnabrechnung - Vorschau
            </DialogTitle>
          </DialogHeader>
          {previewPdf && (
            <div className="space-y-4">
              <iframe src={`data:application/pdf;base64,${previewPdf}`} className="w-full h-[600px] border rounded" title="Lohnabrechnung PDF" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => downloadPdf(previewPdf, `Lohnabrechnung_${employee.nachname}_${payroll.abrechnungsmonat}.pdf`)} data-testid="btn-download-preview">
                  <Download className="w-4 h-4 mr-2" /> Herunterladen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
